import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ConfigReportesDefiniciones.scss';
import { useApiConfiguration } from '../../api/api.configuration';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import DataGrid, { Column, Button as Butt, Editing, Item as Items, Paging, Toolbar, Popup, Lookup, RequiredRule, DataGridTypes, Selection, Form, SearchPanel, Scrolling, Grouping, GroupPanel } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { GroupItem, Item } from 'devextreme-react/form';
import { useScreenSize } from '../../utils/media-query';
import { Cast, SystemFields, Tablas } from '../../types/types.config';
import { useAppSelector } from '../../contexts/hooks';
import { ModalDefiniciones } from './Modals/ModalDefiniciones';
import notify from 'devextreme/ui/notify';

// Equivalencias que NO son dinámicas (campos del sistema)
const EQUIVALENCIAS_NO_DINAMICAS = [
  'Ord_Se_Id', 'Pc_Apellido1', 'Pc_Nombre', 'Pc_Apellido2',
  'Pc_FechaNacimiento', 'Pc_Ti_Id', 'Pc_Gn_Id', 'Ord_FechaCreacion',
];

// Tipos de campos reales del sistema
const TIPOS_CAMPO = [
  { id: 1, name: 'ComboBox' },
  { id: 2, name: 'Date' },
  { id: 3, name: 'Text área' },
  { id: 4, name: 'Text' },
  { id: 5, name: 'Number' },
  { id: 6, name: 'Casilla verificación' },
  { id: 7, name: 'LookUp' },
  { id: 8, name: 'Switch' },
];

// Tipos que son listas y requieren ComboBox (origen de datos)
const TIPOS_LISTA = [1, 7]; // ComboBox, LookUp

// Tipos textuales (admiten Cast y Máscara)
const TIPOS_TEXTUALES = [3, 4]; // Text área, Text

// Tipo numérico (admite Mínimo/Máximo)
const TIPOS_NUMERICOS = [5]; // Number

// Rango de columnas permitido
const NRO_COLUMNAS = [
  { id: 1, name: '1' },
  { id: 2, name: '2' },
  { id: 3, name: '3' },
  { id: 4, name: '4' },
];

const Index = () => {
  const { isMedium, isSmall } = useScreenSize();
  const { getDefiniciones, newDefiniciones, setDefiniciones, deleteDefinicion } = useApiConfiguration();

  const [data, setData] = useState<SystemFields[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [idDefinicion, setIdDefinicion] = useState(-1);
  const dataGridRef = useRef<DataGrid>(null);
  const lstComboBox = useAppSelector((x) => x.configuration.Data?.ComboBox);

  // Tipo de campo vigente en el formulario de edición (incluye cambios sin guardar)
  const editingTipoRef = useRef<number | null>(null);

  const comboBoxData = useMemo(
    () => lstComboBox?.map((item) => ({ id: item.Id, nombre: item.Name })) || [],
    [lstComboBox]
  );

  useEffect(() => {
    getDefiniciones().then((res) => {
      setData(res);
    });
  }, []);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Dfc_Id',
      loadMode: 'processed',
      load() {
        return getDefiniciones()
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      insert: async (values) => {
        // Auto-determinar dinámico según equivalencia
        if (values.Dfc_Equivalencia && EQUIVALENCIAS_NO_DINAMICAS.includes(values.Dfc_Equivalencia)) {
          values.Dfc_Dinamico = false;
        }
        return newDefiniciones({ Definicion: values })
          .then((res) => {
            if (res === 105) {
              return getDefiniciones().then((res) => setData(res));
            }
          })
          .catch(() => {
            throw new Error('Error insertando la definición');
          });
      },
      update: (key, values) => {
        const currentItem = data.find((item) => item.Dfc_Id === key);
        if (!currentItem) {
          throw new Error('No se encontró la definición para actualizar');
        }
        const updatedItem = { ...currentItem, ...values };
        return setDefiniciones({ Definicion: updatedItem, IdDefinicion: key })
          .then((res) => {
            if (res === 107) {
              return getDefiniciones().then(setData);
            }
          })
          .catch(() => {
            throw new Error('Error actualizando la definición');
          });
      },
      remove: (key) => {
        return deleteDefinicion({ Id: key }).then((res) => {
          if (res === 107) {
            return getDefiniciones().then(setData);
          }
        });
      },
    });
  }, [data]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleDetails = useCallback(
    (e: any) => {
      dataGridRef.current?.instance.refresh();
      const { data }: { data: SystemFields } = e.row;
      setIdDefinicion(data.Dfc_Id);
      setIsModalVisible(true);
    },
    [idDefinicion]
  );

  const handleSelection = useCallback(
    ({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
      const dataGrid = selectedRowsData[0];
      setSelectedRow(dataGrid);
    },
    [setSelectedRow]
  );

  const handleUpdateData = useCallback(() => {
    getDefiniciones().then((res) => {
      setData(res);
    });
  }, [setData]);

  // El setCellValue personalizado hace que DevExtreme repinte el formulario de edición,
  // con lo que los editores dependientes se vuelven a preparar con el tipo recién elegido
  const setTipoCellValue = useCallback((newData: any, value: number) => {
    newData.Dfc_Tcm_Id = value;
    editingTipoRef.current = value;

    // Se limpian los campos que dejan de aplicar para el nuevo tipo
    if (!TIPOS_LISTA.includes(value)) newData.Dfc_Tbc_Id = null;
    if (!TIPOS_NUMERICOS.includes(value)) {
      newData.Dfc_Minimo = null;
      newData.Dfc_Maximo = null;
    }
    if (!TIPOS_TEXTUALES.includes(value)) newData.Dfc_Cast = null;
    if (!TIPOS_TEXTUALES.includes(value) && !TIPOS_NUMERICOS.includes(value)) newData.Dfc_Mascara = null;
  }, []);

  // Badge colorido para la columna Tipo
  const renderTipoCelda = useCallback((cellData: any) => {
    const tipo = cellData.value;
    const typeName = TIPOS_CAMPO.find((t) => t.id === tipo)?.name || '—';
    let color = '#6c757d';
    let bg = '#f0f0f0';

    if (TIPOS_LISTA.includes(tipo)) { color = '#007bff'; bg = '#e7f1ff'; }        // ComboBox, LookUp
    else if (TIPOS_TEXTUALES.includes(tipo)) { color = '#28a745'; bg = '#e8f5e9'; } // Text área, Text
    else if (TIPOS_NUMERICOS.includes(tipo)) { color = '#fd7e14'; bg = '#fff3e0'; } // Number
    else if (tipo === 2) { color = '#6f42c1'; bg = '#f3e8ff'; }                     // Date
    else if (tipo === 6) { color = '#17a2b8'; bg = '#e0f7fa'; }                     // Casilla verificación
    else if (tipo === 8) { color = '#e91e63'; bg = '#fce4ec'; }                     // Switch

    return (
      <span className='def-tipo-badge' style={{ background: bg, color }}>
        {typeName}
      </span>
    );
  }, []);

  // Badge para booleanos
  const renderBoolCelda = useCallback((cellData: any) => {
    const val = cellData.value;
    return (
      <span className={`def-bool-badge ${val ? 'is-true' : 'is-false'}`}>
        {val ? 'Sí' : 'No'}
      </span>
    );
  }, []);

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid
          className='grid definiciones-grid'
          dataSource={customDataSrc}
          ref={dataGridRef}
          height='100%'
          rowAlternationEnabled={true}
          showColumnLines={true}
          onSelectionChanged={handleSelection}
          onInitNewRow={(e) => {
            e.data.Dfc_Estado = true;
            e.data.Dfc_Requerido = false;
            e.data.Dfc_Editable = true;
            e.data.Dfc_Dinamico = true;
            e.data.Dfc_Visible = true;
            e.data.Dfc_NroColumnas = 1;
            e.data.Dfc_Orden = (data.length + 1) * 1;
            editingTipoRef.current = null;
          }}
          onEditingStart={(e) => {
            editingTipoRef.current = e.data?.Dfc_Tcm_Id ?? null;
          }}
          onRowRemoving={(e) => {
            if (e.data.Dfc_Dinamico === false) {
              e.cancel = true;
              notify('No se puede eliminar un campo del sistema (Dinámico = No)', 'warning', 3000);
            }
          }}
          onEditorPreparing={(e) => {
            if (e.parentType === 'dataRow') {
              const rowData = e.row?.data || {};
              const tipoId = editingTipoRef.current ?? rowData.Dfc_Tcm_Id;

              // ComboBox solo habilitado si el tipo es lista
              if (e.dataField === 'Dfc_Tbc_Id') {
                e.editorOptions.disabled = !TIPOS_LISTA.includes(tipoId);
              }

              // Min/Max solo para numéricos
              if (e.dataField === 'Dfc_Minimo' || e.dataField === 'Dfc_Maximo') {
                e.editorOptions.disabled = !TIPOS_NUMERICOS.includes(tipoId);
              }

              // Cast solo para textuales
              if (e.dataField === 'Dfc_Cast') {
                e.editorOptions.disabled = !TIPOS_TEXTUALES.includes(tipoId);
              }

              // Máscara solo para textuales y numéricos
              if (e.dataField === 'Dfc_Mascara') {
                e.editorOptions.disabled = !TIPOS_TEXTUALES.includes(tipoId) && !TIPOS_NUMERICOS.includes(tipoId);
              }

              // Dinámico no editable si es equivalencia del sistema
              if (e.dataField === 'Dfc_Dinamico') {
                if (rowData.Dfc_Equivalencia && EQUIVALENCIAS_NO_DINAMICAS.includes(rowData.Dfc_Equivalencia)) {
                  e.editorOptions.disabled = true;
                }
              }
            }
          }}
          onRowValidating={(e) => {
            const tipoId = e.newData?.Dfc_Tcm_Id ?? e.oldData?.Dfc_Tcm_Id;

            // ComboBox obligatorio para tipos lista
            if (TIPOS_LISTA.includes(tipoId)) {
              const comboBoxValue = e.newData?.Dfc_Tbc_Id ?? e.oldData?.Dfc_Tbc_Id;
              if (!comboBoxValue) {
                e.isValid = false;
                e.errorText = 'Debe seleccionar un origen de datos (Combo Box) para campos tipo Lista';
              }
            }

            // Min/Max obligatorio para numéricos
            if (TIPOS_NUMERICOS.includes(tipoId)) {
              const min = e.newData?.Dfc_Minimo ?? e.oldData?.Dfc_Minimo;
              const max = e.newData?.Dfc_Maximo ?? e.oldData?.Dfc_Maximo;
              if (min === null || min === undefined || min === '') {
                e.isValid = false;
                e.errorText = 'El campo Mínimo es obligatorio para campos numéricos';
              }
              if (max === null || max === undefined || max === '') {
                e.isValid = false;
                e.errorText = 'El campo Máximo es obligatorio para campos numéricos';
              }
            }
          }}>
          <SearchPanel visible={true} />
          <Grouping autoExpandAll={true} />
          <GroupPanel visible={true} />
          <Scrolling columnRenderingMode='standard' />
          <Editing
            mode='popup'
            allowAdding
            allowDeleting
            allowUpdating
            texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
            <Popup title='Definición de Campo' width={900} height='auto' maxHeight='90vh' fullScreen={isMedium || isSmall} showTitle={true} />
            <Form colCount={2} labelLocation='top'>
              <GroupItem caption='Información General' colSpan={2} colCount={2}>
                <Item dataField='Dfc_Label' isRequired={true} />
                <Item dataField='Dfc_Tcm_Id' isRequired={true} />
                <Item dataField='Dfc_Tabla' isRequired={true} />
                <Item dataField='Dfc_Equivalencia' isRequired={true} />
                <Item dataField='Dfc_Tbc_Id' />
                <Item dataField='Dfc_Orden' isRequired={true} />
              </GroupItem>
              <GroupItem caption='Visualización' colSpan={1} colCount={2}>
                <Item dataField='Dfc_NroColumnas' />
                <Item dataField='Dfc_Requerido' editorType='dxSwitch' />
                <Item dataField='Dfc_Editable' editorType='dxSwitch' />
                <Item dataField='Dfc_Visible' editorType='dxSwitch' />
                <Item dataField='Dfc_Estado' editorType='dxSwitch' />
                <Item dataField='Dfc_Dinamico' editorType='dxSwitch' />
              </GroupItem>
              <GroupItem caption='Opciones Avanzadas' colSpan={1} colCount={2}>
                <Item dataField='Dfc_Minimo' />
                <Item dataField='Dfc_Maximo' />
                <Item dataField='Dfc_Cast' />
                <Item dataField='Dfc_Mascara' />
              </GroupItem>
            </Form>
          </Editing>
          <Paging enabled={false} />
          <Selection mode='single' />
          <Toolbar>
            <Items location='before'>
              <div className='grid-header'>Definiciones de campos</div>
            </Items>
            <Items name='searchPanel' />
            <Items name='addRowButton' />
            <Items location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RefrecarResult' icon='refresh' text='Actualizar' stylingMode='text' onClick={handleUpdateData} />
              <Tooltip target={'#RefrecarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Items>
          </Toolbar>

          {/* ID oculto */}
          <Column caption='ID' allowEditing={false} visible={false} dataField='Dfc_Id' alignment='center' width={50} />

          {/* ── Columnas visibles en el grid ── */}
          <Column caption='LABEL' dataField='Dfc_Label' minWidth={180}>
            <RequiredRule message='El nombre del campo es obligatorio' />
          </Column>
          <Column caption='TIPO' dataField='Dfc_Tcm_Id' width={160} alignment='center' cellRender={renderTipoCelda} setCellValue={setTipoCellValue}>
            <Lookup dataSource={TIPOS_CAMPO} valueExpr='id' displayExpr='name' />
            <RequiredRule message='Seleccione un tipo de campo' />
          </Column>
          <Column caption='MÓDULO' dataField='Dfc_Tabla' width={120} alignment='center' groupIndex={0}>
            <Lookup dataSource={Tablas} valueExpr='name' displayExpr='name' />
            <RequiredRule message='Seleccione un módulo' />
          </Column>
          <Column caption='EQUIVALENCIA' dataField='Dfc_Equivalencia' minWidth={160}>
            <RequiredRule message='La equivalencia es obligatoria' />
          </Column>
          <Column caption='ORDEN' dataField='Dfc_Orden' width={100} alignment='center' sortOrder='asc'>
            <RequiredRule />
          </Column>
          <Column caption='DINÁMICO' dataField='Dfc_Dinamico' width={95} alignment='center' dataType='boolean' />
          <Column caption='ESTADO' dataField='Dfc_Estado' width={80} alignment='center' dataType='boolean' />

          {/* ── Columnas ocultas en grid, visibles solo en popup ── */}
          <Column caption='ORIGEN LISTA' dataField='Dfc_Tbc_Id' visible={false}>
            <Lookup dataSource={comboBoxData} valueExpr='id' displayExpr='nombre' />
          </Column>
          <Column caption='COLS' dataField='Dfc_NroColumnas' visible={false}>
            <Lookup dataSource={NRO_COLUMNAS} valueExpr='id' displayExpr='name' />
          </Column>
          <Column caption='REQUERIDO' dataField='Dfc_Requerido' visible={false} dataType='boolean' />
          <Column caption='EDITABLE' dataField='Dfc_Editable' visible={false} dataType='boolean' />
          <Column caption='VISIBLE' dataField='Dfc_Visible' visible={false} dataType='boolean' />
          <Column caption='MÍNIMO' dataField='Dfc_Minimo' visible={false} />
          <Column caption='MÁXIMO' dataField='Dfc_Maximo' visible={false} />
          <Column caption='CAST' dataField='Dfc_Cast' visible={false}>
            <Lookup dataSource={Cast} valueExpr='name' displayExpr='name' />
          </Column>
          <Column caption='MÁSCARA' dataField='Dfc_Mascara' visible={false} />

          {/* ── Dependencias – ocultas ── */}
          <Column caption='DEPENDENCIA' dataField='Dfc_Dependency' visible={false}>
            <Lookup dataSource={comboBoxData} valueExpr='id' displayExpr='nombre' />
          </Column>
          <Column caption='DEPENDENCIA CAMPO' dataField='Dfc_DependencyField' visible={false} />
          <Column caption='DEPENDENCIA DEFINICION' dataField='Dfc_DependencyDefinition' visible={false} />

          {/* Acciones */}
          <Column type='buttons' caption='ACCIÓN' width={120}>
            <Butt name='edit' />
            <Butt name='delete' visible={(e: any) => e.row?.data?.Dfc_Dinamico !== false} />
            <Butt hint='Ver dependencias' icon='eyeopen' visible={true} disabled={false} onClick={handleDetails} />
          </Column>
          {isModalVisible && <ModalDefiniciones visible={isModalVisible} setVisible={() => setIsModalVisible(false)} Id={idDefinicion} />}
        </DataGrid>
      </div>
    </div>
  );
};

export default Index;
